import type { RespuestaEncuesta } from '../../api/cxPanelApi';
import { numeroLegible, numeroRespuesta } from './panelCx';

/*
 * Cuestionarios de la campaña compradora (ROC, OC y SI) tal como los ve el cliente en Typeform, para leer sus
 * respuestas en el panel. Enunciados y escalas literales de los formularios públicos (comprobado el 2026-10-08):
 * ROC `SPnyZ20W`, OC `cyah9o` («CX_Atención OpCompra»), SI `lpXk60` («CX_Atención Sol.Info»). Si cambia un
 * formulario, cambiarlo aquí también.
 *
 * Gescom guarda cada respuesta por el código de su pregunta (`GC_PREGUNTA.DS_PREGUNTA`). Las respuestas nuevas
 * (registro automático, 011) solo usan el código de Typeform (`ROC_*`, `OP_*`). Las del sistema anterior traen la
 * misma respuesta repetida en varios códigos, y en tres de ellos como id de opción en vez de la nota; además, todas
 * las ROC/OC/SI llevan `F4_Palabras_*` a 0, preguntas que su cuestionario no tiene. Aquí cada pregunta sale una vez:
 * con el código de Typeform y, si falta, con el primer repetido que tenga valor (los ids se traducen a la nota).
 * Equivalencias comprobadas con todas las OC/SI de pruebas-migracion (1.350 encuestas): ver 012_pr_registradas.sql.
 */

export type TipoPreguntaCompra = 'nps' | 'estrellas' | 'texto';

/** Código de Gescom donde puede estar la respuesta; `idOpcion` = el sistema anterior guardó el id de la opción. */
interface CodigoRespuesta {
  codigo: string;
  /** Id de la opción que corresponde a la nota `nota0` y última id válida (ids consecutivos). */
  idOpcion?: { primero: number; nota0: number; ultimo: number };
}

export interface PreguntaCompra {
  /** Código principal (el de Typeform): sirve de clave. */
  clave: string;
  tipo: TipoPreguntaCompra;
  texto: string;
  /** Códigos con la respuesta, por preferencia: el de Typeform primero; detrás, los repetidos del sistema anterior. */
  codigos: CodigoRespuesta[];
}

const NPS_COMPANEROS = '¿Con qué probabilidad nos recomendarías a tus compañeros farmacéuticos?';
const COMENTARIO = 'Si quieres compartir algún comentario con nosotros, puedes hacerlo aquí. Nos encantaría conocer tu opinión.';

/** OC y SI: mismo formulario salvo el enunciado del NPS. */
const atencion = (textoNps: string): PreguntaCompra[] => [
  {
    clave: 'OP_Recomendacion', tipo: 'nps', texto: textoNps,
    codigos: [
      { codigo: 'OP_Recomendacion' },
      { codigo: 'F2_Recomendacion' },
      { codigo: 'RecomendarRev_num' },
      { codigo: 'Recomendar_asesoramiento', idOpcion: { primero: 1054, nota0: 0, ultimo: 1064 } },
    ],
  },
  {
    clave: 'OP_SolucionarDudas', tipo: 'estrellas', texto: '¿Hemos solucionado tus dudas de manera clara y concisa?',
    codigos: [
      { codigo: 'OP_SolucionarDudas' },
      { codigo: 'Solucionar_dudas', idOpcion: { primero: 1231, nota0: 0, ultimo: 1236 } },
    ],
  },
  {
    clave: 'OP_RapidezAtencion', tipo: 'estrellas', texto: '¿Te ha parecido rápido nuestro servicio de atención?',
    codigos: [
      { codigo: 'OP_RapidezAtencion' },
      // 1237 es la nota 1 (no hay 0 en esta pregunta).
      { codigo: 'Rapidez_atencion', idOpcion: { primero: 1237, nota0: 1, ultimo: 1241 } },
    ],
  },
  {
    clave: 'OP_AspectosMejorar', tipo: 'texto', texto: COMENTARIO,
    codigos: [{ codigo: 'OP_AspectosMejorar' }, { codigo: 'F2_AspectosMejorar' }, { codigo: 'Mejoras' }],
  },
];

export const CUESTIONARIOS_COMPRA: Record<'ROC' | 'OC' | 'SI', PreguntaCompra[]> = {
  ROC: [
    {
      clave: 'ROC_ProyectoVida', tipo: 'estrellas', codigos: [{ codigo: 'ROC_ProyectoVida' }],
      texto: 'Hemos comprendido tus necesidades y preocupaciones respecto a tu proyecto de vida.',
    },
    {
      clave: 'ROC_CumplirObjetivos', tipo: 'estrellas', codigos: [{ codigo: 'ROC_CumplirObjetivos' }],
      texto: 'Contamos con el equipo, herramientas, y valores que necesitas para ayudarte a cumplir tus objetivos.',
    },
    {
      clave: 'ROC_Fct_n1', tipo: 'estrellas', codigos: [{ codigo: 'ROC_Fct_n1' }],
      texto: 'Farmaconsulting es el Nº1 en transmisiones y el único con cobertura nacional.',
    },
    {
      clave: 'ROC_Elegir_Fct', tipo: 'estrellas', codigos: [{ codigo: 'ROC_Elegir_Fct' }],
      texto: 'Frente a otras opciones, elegiría a Farmaconsulting para la compra de mi farmacia',
    },
    { clave: 'ROC_Recomendacion', tipo: 'nps', codigos: [{ codigo: 'ROC_Recomendacion' }], texto: NPS_COMPANEROS },
    { clave: 'ROC_Notas', tipo: 'texto', codigos: [{ codigo: 'ROC_Notas' }], texto: COMENTARIO },
  ],
  OC: atencion(NPS_COMPANEROS),
  SI: atencion('¿Con qué probabilidad nos recomendarías a otra persona?'),
};

/** Códigos que estas encuestas traen del sistema anterior sin ser de su cuestionario (siempre 0): no se enseñan. */
const IGNORADAS = /^F4_Palabras_/;

/** Escala de cada tipo de pregunta (para pintar y para descartar valores fuera de rango). */
export const ESCALA: Record<Exclude<TipoPreguntaCompra, 'texto'>, { min: number; max: number }> = {
  nps: { min: 0, max: 10 },
  estrellas: { min: 0, max: 5 }, // el sistema anterior tiene algún 0 en «solucionar dudas»
};

/** Valor de un código: la nota (ids traducidos, sin decimales) o el texto. Null = sin respuesta. */
const valorDe = (tipo: TipoPreguntaCompra, c: CodigoRespuesta, bruto?: string | null): string | null => {
  const v = (bruto ?? '').trim();
  if (v === '') return null;
  if (tipo === 'texto') return v;
  const n = numeroRespuesta(v);
  if (n == null) return null;
  if (c.idOpcion) {
    // Ids de opción del sistema anterior; 0 y -1 = sin respuesta. Un id fuera de la lista no se enseña.
    if (n < c.idOpcion.primero || n > c.idOpcion.ultimo) return null;
    return String(n - c.idOpcion.primero + c.idOpcion.nota0);
  }
  const { min, max } = ESCALA[tipo];
  return n >= min && n <= max ? numeroLegible(n) : null;
};

export interface RespuestaCompra {
  pregunta: PreguntaCompra;
  /** Nota («9») o texto; null si no la contestó. */
  valor: string | null;
}

export interface LecturaCompra {
  /** Todas las preguntas del cuestionario, en su orden, con su respuesta (o null). */
  respuestas: RespuestaCompra[];
  /** Respuestas con códigos que no son de este cuestionario ni repetidos conocidos (se enseñan aparte). */
  otras: RespuestaEncuesta[];
}

/** ¿Tiene la encuesta un cuestionario de compra (ROC, OC o SI)? */
export const esTipoCompra = (tipo: string): tipo is 'ROC' | 'OC' | 'SI' => tipo === 'ROC' || tipo === 'OC' || tipo === 'SI';

/**
 * Lee las respuestas de una ROC, OC o SI como el cuestionario que ve el cliente: cada pregunta una vez, en su orden,
 * sin códigos repetidos ni ids de opción. Null si el tipo no es de la campaña compradora.
 */
export const leerCuestionarioCompra = (tipo: string, respuestas: RespuestaEncuesta[]): LecturaCompra | null => {
  if (!esTipoCompra(tipo)) return null;
  const preguntas = CUESTIONARIOS_COMPRA[tipo];
  const porCodigo = new Map<string, string | null>();
  respuestas.forEach((r) => {
    const codigo = r.pregunta.trim();
    // Si un código viniera dos veces, vale el primero con valor.
    if (!porCodigo.get(codigo)) porCodigo.set(codigo, r.respuesta ?? null);
  });

  const conocidos = new Set(preguntas.flatMap((p) => p.codigos.map((c) => c.codigo)));
  const leidas = preguntas.map((p) => {
    for (const c of p.codigos) {
      const v = valorDe(p.tipo, c, porCodigo.get(c.codigo));
      if (v != null) return { pregunta: p, valor: v };
    }
    return { pregunta: p, valor: null };
  });

  const vistas = new Set<string>();
  const otras = respuestas.filter((r) => {
    const codigo = r.pregunta.trim();
    if (conocidos.has(codigo) || IGNORADAS.test(codigo) || vistas.has(codigo)) return false;
    vistas.add(codigo);
    return (r.respuesta ?? '').trim() !== '';
  });

  return { respuestas: leidas, otras };
};
